/**
 * Alanube Registration Page
 *
 * Mobile-first 4-step wizard for registering a company in Alanube:
 * 1. Verify company data
 * 2. Upload the cryptographic key (.p12/.pfx) generated in TRIBU-CR
 * 3. Enter the electronic-voucher credentials (also from TRIBU-CR)

 * 4. Confirm and submit registration
 */

import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import {
  Shield,
  Building2,
  Upload,
  Key,
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  FileKey,
  Mail,
  ChevronLeft,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/toaster'
import { useTenant } from '@/hooks/useTenant'
import { useTranslation } from '@/hooks/useTranslation'
import { TribuCrGuide } from '@/components/settings/TribuCrGuide'
import { cn } from '@/lib/utils'
import api from '@/lib/api'

interface RegistrationResponse {
  success: boolean
  company_id?: string
  certificate_status?: string
  certificate_expiry?: string
  certificate_serial?: string
  error?: string
  error_details?: Record<string, unknown>
}

const STEPS = [
  { id: 1, nameKey: 'settings.registration.stepCompany', icon: Building2 },
  { id: 2, nameKey: 'settings.registration.stepKey', icon: FileKey },
  { id: 3, nameKey: 'settings.registration.stepCredentials', icon: Key },
  { id: 4, nameKey: 'settings.registration.stepConfirm', icon: CheckCircle2 },
]

const IDENTIFICATION_TYPES = ['01', '02', '03', '04', '05']

export function AlanubeRegistrationPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { toast } = useToast()
  const { tenant, refreshTenant } = useTenant()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Update tenant location mutation
  const updateLocationMutation = useMutation({
    mutationFn: async (data: { province_id: number; canton_id: number; district_id: number }) => {
      const response = await api.patch('/tenants/me', data)
      return response.data
    },
    onSuccess: () => {
      refreshTenant()
    },
  })

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1)

  // Certificate state
  const [certificateFile, setCertificateFile] = useState<File | null>(null)
  const [certificatePassword, setCertificatePassword] = useState('')
  const [showCertPassword, setShowCertPassword] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  // Credenciales de comprobantes (TRIBU-CR)
  const [atvUsername, setAtvUsername] = useState('')
  const [atvPassword, setAtvPassword] = useState('')
  const [showAtvPassword, setShowAtvPassword] = useState(false)

  // Registration mutation
  const registerMutation = useMutation({
    mutationFn: async () => {
      if (!certificateFile) {
        throw new Error(t('settings.registration.noFileSelected'))
      }

      const formData = new FormData()
      formData.append('certificate', certificateFile)
      formData.append('certificate_password', certificatePassword)
      formData.append('atv_username', atvUsername)
      formData.append('atv_password', atvPassword)

      const response = await api.post('/tenants/register-alanube', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data as RegistrationResponse
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: t('settings.registration.successTitle'),
          description: t('settings.companyRegisteredOk'),
        })
        refreshTenant()
        navigate('/settings')
      } else {
        toast({
          title: t('settings.registration.errorTitle'),
          description: data.error || t('settings.companyRegisterError'),
          variant: 'destructive',
        })
      }
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error'),
        description: error.message || t('settings.companyRegisterError'),
        variant: 'destructive',
      })
    },
  })

  // Drag and drop handlers
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && isValidCertificateFile(droppedFile.name)) {
      setCertificateFile(droppedFile)
    } else {
      toast({
        title: t('settings.registration.invalidFile'),
        description: t('settings.registration.invalidFileHint'),
        variant: 'destructive',
      })
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setCertificateFile(selectedFile)
    }
  }

  const isValidCertificateFile = (filename: string) => {
    const lower = filename.toLowerCase()
    return lower.endsWith('.p12') || lower.endsWith('.pfx')
  }

  // Validation checks for each step
  const validateStep1 = () => {
    if (!tenant) return false
    return !!(
      tenant.name &&
      tenant.identification &&
      tenant.identification_type &&
      tenant.email
    )
  }

  const validateStep2 = () => {
    return certificateFile !== null && certificatePassword.length > 0
  }

  const validateStep3 = () => {
    return atvUsername.length > 0 && atvPassword.length > 0
  }

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return validateStep1()
      case 2:
        return validateStep2()
      case 3:
        return validateStep3()
      case 4:
        return true
      default:
        return false
    }
  }

  const handleNext = () => {
    if (currentStep < 4 && canProceed()) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = () => {
    registerMutation.mutate()
  }

  const isSandbox = tenant?.alanube_environment === 'sandbox'

  // Step 1: Company Data Verification
  const renderStep1 = () => {
    const isComplete = validateStep1()
    const missingFields: string[] = []

    if (!tenant?.name) missingFields.push(t('settings.registration.legalName'))
    if (!tenant?.identification) missingFields.push(t('settings.registration.identification'))
    if (!tenant?.email) missingFields.push(t('settings.registration.email'))

    return (
      <div className="space-y-4">
        {/* Status Card */}
        <div
          className={cn(
            'p-4 rounded-lg border',
            isComplete
              ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
              : 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950'
          )}
        >
          <div className="flex items-start gap-3">
            {isComplete ? (
              <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium">
                {isComplete
                  ? t('settings.registration.dataComplete')
                  : t('settings.registration.dataIncomplete')}
              </p>
              <p className="text-sm text-muted-foreground">
                {isComplete
                  ? t('settings.registration.dataCompleteHint')
                  : t('settings.registration.missingFields', { fields: missingFields.join(', ') })}
              </p>
            </div>
          </div>
        </div>

        {/* Company Data Section */}
        {tenant && (
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
            {/* Company Name & ID */}
            <div className="flex items-center gap-2 pb-2 border-b">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="font-medium">{t('settings.registration.companyData')}</span>
              {updateLocationMutation.isPending && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ml-auto" />
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('settings.registration.legalName')}</Label>
              <p className="font-medium truncate">{tenant.display_name}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t('settings.registration.identification')}</Label>
                <p className="font-mono">{tenant.identification}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t('settings.registration.identificationType')}</Label>
                <Badge variant="outline">
                  {IDENTIFICATION_TYPES.includes(tenant.identification_type)
                    ? t(`idTypes.type${tenant.identification_type}`)
                    : tenant.identification_type}
                </Badge>
              </div>
            </div>

            {/* Email */}
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('settings.registration.email')}</Label>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="truncate">{tenant.email || t('settings.registration.notConfigured')}</span>
              </div>
            </div>

            {/* Hacienda Config */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 pb-3">
                <Building2 className="h-4 w-4 text-primary" />
                <span className="font-medium">{t('settings.registration.haciendaConfig')}</span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('settings.registration.branch')}</Label>
                  <p className="font-mono">{tenant.headquarters}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('settings.registration.terminal')}</Label>
                  <p className="font-mono">{tenant.terminal}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Step 2: Cryptographic key upload
  const renderStep2 = () => {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('settings.registration.keyIntro')}</p>

        <TribuCrGuide topic="key" isSandbox={isSandbox} />

        {/* File upload area */}
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-6 text-center transition-colors min-h-[160px] flex items-center justify-center',
            dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
            certificateFile && 'border-green-500 bg-green-50 dark:bg-green-950'
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !certificateFile && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".p12,.pfx"
            onChange={handleFileChange}
            className="hidden"
          />

          {certificateFile ? (
            <div className="space-y-3">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
              <p className="font-medium text-green-700 dark:text-green-300 truncate max-w-[250px] mx-auto">
                {certificateFile.name}
              </p>
              <p className="text-sm text-muted-foreground">
                {(certificateFile.size / 1024).toFixed(1)} KB
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  setCertificateFile(null)
                }}
              >
                {t('settings.registration.changeFile')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Upload className="h-12 w-12 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground hidden sm:block">
                {t('settings.registration.dropHere')}
              </p>
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef.current?.click()
                }}
              >
                {t('settings.registration.selectFile')}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t('settings.registration.formats')}
              </p>
            </div>
          )}
        </div>

        {/* Password input */}
        <div className="space-y-2">
          <Label htmlFor="cert-password">{t('settings.registration.keyPin')}</Label>
          <div className="relative">
            <Input
              id="cert-password"
              type={showCertPassword ? 'text' : 'password'}
              autoComplete="one-time-code"
              placeholder={t('settings.registration.keyPinPlaceholder')}
              value={certificatePassword}
              onChange={(e) => setCertificatePassword(e.target.value)}
              className="h-12 pr-12"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-12 w-12"
              onClick={() => setShowCertPassword(!showCertPassword)}
            >
              {showCertPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            {t('settings.certificateNotStoredLocally')}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // Step 3: Electronic-voucher credentials (TRIBU-CR)
  const renderStep3 = () => {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('settings.registration.credentialsIntro')}
        </p>

        <TribuCrGuide topic="credentials" isSandbox={isSandbox} />

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="atv-username">{t('settings.registration.username')}</Label>
            <Input
              id="atv-username"
              type="text"
              placeholder={
                isSandbox
                  ? t('settings.registration.usernameExampleSandbox')
                  : t('settings.registration.usernameExampleProduction')
              }
              value={atvUsername}
              onChange={(e) => setAtvUsername(e.target.value)}
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="atv-password">{t('settings.registration.password')}</Label>
            <div className="relative">
              <Input
                id="atv-password"
                type={showAtvPassword ? 'text' : 'password'}
                placeholder={t('settings.registration.passwordPlaceholder')}
                value={atvPassword}
                onChange={(e) => setAtvPassword(e.target.value)}
                autoComplete="new-password"
                data-1p-ignore
                data-lpignore="true"
                className="h-12 pr-12"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-12 w-12"
                onClick={() => setShowAtvPassword(!showAtvPassword)}
              >
                {showAtvPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        <Alert>
          <Key className="h-4 w-4" />
          <AlertDescription>
            {t('settings.registration.credentialsPurpose')}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // Step 4: Confirmation
  const renderStep4 = () => {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('settings.reviewBeforeRegistering')}
        </p>

        {/* Summary */}
        <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
          <h4 className="font-medium">{t('settings.registration.summaryTitle')}</h4>

          <div className="space-y-3 text-sm">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('settings.registration.summaryCompany')}</span>
              <span className="font-medium truncate">{tenant?.display_name}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('settings.registration.summaryIdentification')}</span>
              <span className="font-mono">{tenant?.identification}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('settings.registration.summaryKey')}</span>
              <span className="text-green-600 dark:text-green-400 flex items-center gap-1 truncate">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="truncate">{certificateFile?.name}</span>
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('settings.registration.summaryUsername')}</span>
              <span className="truncate">{atvUsername}</span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-muted-foreground">{t('settings.registration.summaryEnvironment')}</span>
              <Badge variant={isSandbox ? 'secondary' : 'default'} className="w-fit">
                {isSandbox ? t('settings.environmentSandbox') : t('settings.environmentProduction')}
              </Badge>
            </div>
          </div>
        </div>

        {isSandbox && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('settings.registration.sandboxTitle')}</AlertTitle>
            <AlertDescription>
              {t('settings.registration.sandboxHint')}
            </AlertDescription>
          </Alert>
        )}

        {registerMutation.isError && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertTitle>{t('settings.registration.errorTitle')}</AlertTitle>
            <AlertDescription>
              {registerMutation.error?.message || t('settings.companyRegisterError')}
            </AlertDescription>
          </Alert>
        )}
      </div>
    )
  }

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1()
      case 2:
        return renderStep2()
      case 3:
        return renderStep3()
      case 4:
        return renderStep4()
      default:
        return null
    }
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            className="shrink-0"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold flex items-center gap-2">
              <Shield className="h-5 w-5 shrink-0" />
              <span className="truncate">{t('settings.registerCompany')}</span>
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('settings.registration.stepOf', { current: currentStep, total: STEPS.length })}
            </p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-3">
          <div className="flex justify-between items-center mb-2">
            {STEPS.map((step) => {
              const StepIcon = step.icon
              const isActive = currentStep === step.id
              const isComplete = currentStep > step.id

              return (
                <div key={step.id} className="flex flex-col items-center gap-1">
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
                      isActive && 'bg-primary text-primary-foreground',
                      isComplete && 'bg-green-500 text-white',
                      !isActive && !isComplete && 'bg-muted text-muted-foreground'
                    )}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="h-4 w-4" />
                    ) : (
                      <StepIcon className="h-4 w-4" />
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-[10px] text-center leading-tight',
                      isActive && 'text-primary font-medium',
                      isComplete && 'text-green-600 dark:text-green-400',
                      !isActive && !isComplete && 'text-muted-foreground'
                    )}
                  >
                    {t(step.nameKey)}
                  </span>
                </div>
              )
            })}
          </div>
          <Progress value={(currentStep / 4) * 100} className="h-1.5" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {renderStepContent()}
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 z-10 bg-background border-t p-4 space-y-2">
        {currentStep < 4 ? (
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="w-full h-12"
          >
            {t('common.next')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={registerMutation.isPending}
            className="w-full h-12"
          >
            {registerMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('settings.registration.registering')}
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {t('settings.registerCompany')}
              </>
            )}
          </Button>
        )}

        <div className="flex gap-2">
          {currentStep > 1 && (
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={registerMutation.isPending}
              className="flex-1 h-12"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('settings.registration.back')}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate('/settings')}
            disabled={registerMutation.isPending}
            className="flex-1 h-12"
          >
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export { AlanubeRegistrationPage as default }
